import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getPurchaseOrders, savePurchaseOrder, getNextInvoiceNo, getPurchaseOrderForReturn, getPurchaseReturnById } from "../../components/Api/GmsPurchaseOrder";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getSuppliers, searchSuppliers } from "../../components/Api/GmsSupplier";
import { getProducts } from "../../components/Api/GmsProduct";
import { getActiveUnits } from "../../components/Api/GmsUnits";
import { getTaxes } from "../../components/Api/GmsTax";
import { getProductPreviousPrices } from "../../components/Api/GmsProductPrices";
import { Eye, SquarePen, Plus, Minus, X, RotateCcw } from "lucide-react";
import StockSelectionModal from "../../components/StockSelectionModal";
import StockDetailsModal from "../../components/StockDetailsModal";
import { checkStockSelectionLocks, deleteStockSelectionLock } from "../../components/Api/GmsStockSelectionLock";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  PurchaseOrder_Id: 0,
  PurchaseOrder_InvoiceNo: "",
  PurchaseOrder_RefNo: "",
  PurchaseOrder_Supplier_Id: null,
  PurchaseOrder_InvoiceType_Id: 1, // 1 = Cash, 2 = Credit
  PurchaseOrder_Date: new Date(),
  PurchaseOrder_DueDate: new Date(),
  PurchaseOrder_Amount: 0,
  PurchaseOrder_DiscountPercent: 0,
  PurchaseOrder_DiscountAmount: 0,
  PurchaseOrder_OtherAmount: 0,
  PurchaseOrder_TaxAmount: 0,
  PurchaseOrder_OtherCharges: 0,
  PurchaseOrder_AdditionalDiscount: 0,
  PurchaseOrder_NetAmount: 0,
  PurchaseOrder_Notes: "",
  PurchaseOrder_TotalQty: 0,
  PurchaseOrder_Branch_Id: null,
  PurchaseOrder_TotalAmount: 0,
  PurchaseOrder_CreatedDate: new Date().toISOString(),
  PurchaseOrder_CreatedBy: 1,
  PurchaseOrder_Business_Id: null,
  Details: [],
};

const emptyFilter = {
  PurchaseOrder_Id: null,
  PurchaseOrder_InvoiceNo: null,
  PurchaseOrder_Supplier_Id: null,
  PurchaseOrder_Business_Id: null,
  From_Date: null,
  To_Date: null,
};

const defaultDetail = {
  PurchaseOrderDet_Id: 0,
  PurchaseOrderDet_Product_Id: null,
  PurchaseOrderDet_Unit_Id: 1,
  PurchaseOrderDet_Quantity: 1,
  PurchaseOrderDet_BonusQuantity: 0,
  PurchaseOrderDet_UnitPrice: 0,
  PurchaseOrderDet_DiscountPercent: 0,
  PurchaseOrderDet_DiscountAmount: 0,
  PurchaseOrderDet_TotalAmount: 0, // Added TotalAmount field
  PurchaseOrderDet_NetAmount: 0,
  PurchaseOrderDet_TaxPercent: 0,
  Product_NameEn: "",
  Unit_NameEn: "",
  TaxDetails: [],
};

const defaultStock = {
  PurchaseStock_BatchNo: "",
  PurchaseStock_ExpDate: null,
  PurchaseStock_SalesPrice: 0,
  PurchaseStock_CostPrice: 0,
  PurchaseStock_IsBonus: false,
};

// Date validation function for DD/MM/YYYY format
const isValidDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') {
    return false;
  }
  
  // Check format DD/MM/YYYY
  const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = dateStr.match(datePattern);
  
  if (!match) {
    return false;
  }
  
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  
  // Check if month is valid (1-12)
  if (month < 1 || month > 12) {
    return false;
  }
  
  // Check if day is valid for the month
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) {
    return false;
  }
  
  // Check if year is reasonable (e.g., between 1900 and 2100)
  if (year < 1900 || year > 2100) {
    return false;
  }
  
  // Create a date object to verify it's a valid date
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return false;
  }
  
  return true;
};

export default function PurchasePage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsPurchase');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsPurchase');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsPurchase');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsPurchase');
  const isAdmin = isUserAdmin();

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [details, setDetails] = useState([defaultDetail]);
  
  // Debug logging removed to reduce console noise
  
  

  // Helper function to get field value with fallback
  const getDetailField = (detail, fieldName) => {
    // Handle special cases for product and unit names
    if (fieldName === 'Product_NameEn') {
      return detail.Product_NameEn || detail.product_NameEn || '';
    }
    if (fieldName === 'Unit_NameEn') {
      return detail.Unit_NameEn || detail.unit_NameEn || '';
    }
    // For other fields, try multiple variations
    if (detail[fieldName] !== undefined) {
      return detail[fieldName];
    }
    const lowercaseFieldName = fieldName.replace('PurchaseOrderDet_', 'purchaseOrderDet_');
    if (detail[lowercaseFieldName] !== undefined) {
      return detail[lowercaseFieldName];
    }
    const lowercaseFieldName2 = fieldName.replace('PurchaseOrder_', 'purchaseOrder_');
    if (detail[lowercaseFieldName2] !== undefined) {
      return detail[lowercaseFieldName2];
    }
    const lowercaseFieldName3 = fieldName.replace('PurchaseStock_', 'purchaseStock_');
    if (detail[lowercaseFieldName3] !== undefined) {
      return detail[lowercaseFieldName3];
    }
    const lowercaseFieldName4 = fieldName.replace('PurchaseOrderTaxDet_', 'purchaseOrderTaxDet_');
    if (detail[lowercaseFieldName4] !== undefined) {
      return detail[lowercaseFieldName4];
    }
    return '';
  };

  // Helper function to format numeric value for display (empty string when 0)
  const formatNumericValue = (value) => {
    const numValue = parseFloat(value) || 0;
    return numValue === 0 ? '' : numValue;
  };
  const [isEditing, setIsEditing] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isReturnMode, setIsReturnMode] = useState(false);
  const [returnPurchaseOrderId, setReturnPurchaseOrderId] = useState(null);
  const [originalInvoiceNo, setOriginalInvoiceNo] = useState(null);
  // Stock selection lock - unique form ID (only for return mode)
  const [temporaryFormUniqueId, setTemporaryFormUniqueId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [formKey, setFormKey] = useState(0);
  
  // Supplier search states
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [supplierSearchTimeout, setSupplierSearchTimeout] = useState(null);
  const [selectedSupplierOption, setSelectedSupplierOption] = useState(null);
  const [selectedSupplierFilterOption, setSelectedSupplierFilterOption] = useState(null);
  
  // Modal states
  const [showStockModal, setShowStockModal] = useState(false);
  const [showStockSelectionModal, setShowStockSelectionModal] = useState(false);
  const [currentDetailIndex, setCurrentDetailIndex] = useState(0);
  const [stockDetails, setStockDetails] = useState([]);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [previousPrices, setPreviousPrices] = useState(null);
  const [selectedStockForReturn, setSelectedStockForReturn] = useState({});
  const [preloadedStockItems, setPreloadedStockItems] = useState(null);
  // Stock details modal (view-only)
  const [showStockDetailsModal, setShowStockDetailsModal] = useState(false);
  const [currentStockDetails, setCurrentStockDetails] = useState([]);
  const [currentStockDetailsProduct, setCurrentStockDetailsProduct] = useState("");

  // Rounding helper: standard half-up to 2 decimals
  const roundHalfUp = (value, decimals = 2) => {
    const factor = Math.pow(10, decimals);
    return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
  };

  // Format date as dd/MM/yyyy
  const formatDateDDMMYYYY = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  // Generate unique form ID for stock selection lock (Purchase Return only)
  const generateTemporaryFormUniqueId = () => {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, ''); // HHMMSS
    const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const formId = `PR-${dateStr}-${timeStr}-${sequence}`;
    
    // Store form ID in sessionStorage so we can clean it up on refresh
    const storedFormIds = JSON.parse(sessionStorage.getItem('stockLockFormIds') || '[]');
    storedFormIds.push(formId);
    sessionStorage.setItem('stockLockFormIds', JSON.stringify(storedFormIds));
    
    return formId;
  };

  useEffect(() => {
    if (hasPermission) {
      // Cleanup stock locks on page load/refresh
      // Since the form is cleared, we should also clear any locks from previous session
      const cleanupLocksOnRefresh = async () => {
        try {
          const { cleanupStockSelectionLocks, deleteStockSelectionLock } = await import('../components/Api/GmsStockSelectionLock');
          
          // 1. Clean up any form IDs stored in sessionStorage (from previous session before refresh)
          const storedFormIds = JSON.parse(sessionStorage.getItem('stockLockFormIds') || '[]');
          console.log('🔒 Cleaning up locks for stored form IDs:', storedFormIds);
          for (const formId of storedFormIds) {
            try {
              await deleteStockSelectionLock(formId);
              console.log('✅ Cleaned up locks for form:', formId);
            } catch (err) {
              console.warn('Failed to cleanup locks for form:', formId, err);
            }
          }
          // Clear the stored form IDs
          sessionStorage.removeItem('stockLockFormIds');
          
          // 2. Also clean up locks older than 5 minutes (very short time to catch any missed ones)
          // This ensures any locks from the previous session are cleared
          await cleanupStockSelectionLocks(0.083).catch(err => { // 0.083 hours = 5 minutes
            console.warn('Failed to cleanup old locks on page load:', err);
          });
          console.log('✅ Cleaned up locks older than 5 minutes');
        } catch (error) {
          console.warn('Error during lock cleanup on page load:', error);
        }
      };
      cleanupLocksOnRefresh();
      
      loadBusinesses();
      loadBranches();
      loadSuppliers();
      loadProducts();
      loadUnits();
      loadTaxes();
      loadPurchaseOrders();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadPurchaseOrders();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  // Cleanup stock locks on page refresh/navigation (only for return mode)
  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (isReturnMode && temporaryFormUniqueId) {
        try {
          await deleteStockSelectionLock(temporaryFormUniqueId);
        } catch (error) {
          console.error('Error cleaning up locks on page unload:', error);
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also cleanup on component unmount
      if (isReturnMode && temporaryFormUniqueId) {
        deleteStockSelectionLock(temporaryFormUniqueId).catch(err => 
          console.error('Error cleaning up locks on unmount:', err)
        );
      }
    };
  }, [isReturnMode, temporaryFormUniqueId]);

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({});
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load companies", "error");
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({});
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await getSuppliers({});
      setSuppliers(data || []);
    } catch (error) {
      console.error("Error loading suppliers:", error);
      Swal.fire("Error", "Failed to load suppliers", "error");
    }
  };

  // Manual search function for supplier search
  const searchSuppliersManually = async (inputValue) => {
    console.log('🔍 searchSuppliersManually called with inputValue:', inputValue);
    
    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';
    
    if (!trimmedInput || trimmedInput.length < 3) {
      console.log('❌ Input too short, clearing options');
      setSupplierSearchOptions([]);
      return;
    }
    
    try {
      setSupplierSearchLoading(true);
      console.log('🚀 Making API call to searchSuppliers with:', { trimmedInput, page: 1, pageSize: 20 });
      const response = await searchSuppliers(trimmedInput, 1, 20);
      console.log('✅ API response received:', response);
      
      if (!response || !response.items || !Array.isArray(response.items)) {
        console.log('❌ Invalid response format:', response);
        setSupplierSearchOptions([]);
        return;
      }
      
      const formattedOptions = response.items.map(supplier => {
        // Check if search text appears in Arabic name (handle Arabic text properly)
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';
        
        // Check if search text contains Arabic characters or matches Arabic name
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) || 
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );
        
        // Prioritize Arabic name if search matches Arabic, otherwise show English first
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          // Search matched Arabic name - show Arabic first
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          // Search matched English name or code - show English first
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          // Only Arabic name available
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }
        
        // Create label with both names if available
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || supplier.supplier_ContactNo || 'N/A'}`;
        
        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });
      
      console.log('📋 Formatted options:', formattedOptions);
      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  // Manual search function for supplier filter search
  const searchSuppliersFilterManually = async (inputValue) => {
    console.log('🔍 searchSuppliersFilterManually called with inputValue:', inputValue);
    
    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';
    
    if (!trimmedInput || trimmedInput.length < 3) {
      console.log('❌ Input too short, clearing options');
      setSupplierSearchOptions([]);
      return;
    }
    
    try {
      setSupplierSearchLoading(true);
      console.log('🚀 Making API call to searchSuppliers with:', { trimmedInput, page: 1, pageSize: 20 });
      const response = await searchSuppliers(trimmedInput, 1, 20);
      console.log('✅ API response received:', response);
      
      if (!response || !response.items || !Array.isArray(response.items)) {
        console.log('❌ Invalid response format:', response);
        setSupplierSearchOptions([]);
        return;
      }
      
      const formattedOptions = response.items.map(supplier => {
        // Check if search text appears in Arabic name (handle Arabic text properly)
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';
        
        // Check if search text contains Arabic characters or matches Arabic name
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) || 
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );
        
        // Prioritize Arabic name if search matches Arabic, otherwise show English first
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          // Search matched Arabic name - show Arabic first
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          // Search matched English name or code - show English first
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          // Only Arabic name available
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }
        
        // Create label with both names if available
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || supplier.supplier_ContactNo || 'N/A'}`;
        
        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });
      
      console.log('📋 Formatted options:', formattedOptions);
      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts({});
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
      Swal.fire("Error", "Failed to load products", "error");
    }
  };

  const loadUnits = async () => {
    try {
      const data = await getActiveUnits();
      setUnits(data || []);
    } catch (error) {
      console.error("Error loading units:", error);
      Swal.fire("Error", "Failed to load units", "error");
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ tax_Active: true });
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
      Swal.fire("Error", "Failed to load taxes", "error");
    }
  };

  const loadPurchaseOrders = async () => {
    try {
      setLoading(true);
      const data = await getPurchaseOrders(filter);
      setPurchaseOrders(data || []);
      setTotalCount(data?.length || 0);
    } catch (error) {
      console.error("Error loading purchase:", error);
      Swal.fire("Error", "Failed to load purchase", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessChange = async (businessId) => {
    setForm(prev => ({
      ...prev,
      PurchaseOrder_Business_Id: businessId,
      PurchaseOrder_Branch_Id: null
    }));

    if (businessId) {
      try {
        const result = await getNextInvoiceNo(businessId, isReturnMode, isReturnMode ? originalInvoiceNo : null);
        console.log("Invoice number result:", result);
        if (result.nextInvoiceNo) {
          setForm(prev => ({
            ...prev,
            PurchaseOrder_InvoiceNo: result.nextInvoiceNo
          }));
        }
      } catch (error) {
        console.error("Error getting next invoice number:", error);
      }
    }
  };

  const handlePurchaseReturn = async (purchaseOrder) => {
    try {
      setLoading(true);
      // IMPORTANT: We fetch FRESH data from database on click to handle race conditions
      // This ensures we get the latest stock status even if another user returned stocks
      // after the purchase list was loaded. The stored procedure GMS_Select_PurchaseOrder_ForReturn
      // filters out already returned stocks (LastTransFlag NOT IN 3, 4, 7) at query time.
      const purchaseOrderData = await getPurchaseOrderForReturn(purchaseOrder.purchaseOrder_Id || purchaseOrder.PurchaseOrder_Id);
      
      if (!purchaseOrderData) {
        Swal.fire("Error", "Failed to load purchase order for return", "error");
        setLoading(false);
        return;
      }

      // Check if there are any returnable stocks
      // The stored procedure already filters out returned stocks (LastTransFlag NOT IN 3, 4, 7)
      // So we check if any detail has stockDetails with returnable stocks
      // This check uses FRESH data from the database, not cached list data
      let hasReturnableStocks = false;
      if (purchaseOrderData.details && purchaseOrderData.details.length > 0) {
        hasReturnableStocks = purchaseOrderData.details.some(detail => 
          detail.stockDetails && 
          Array.isArray(detail.stockDetails) && 
          detail.stockDetails.length > 0
        );
      }

      if (!hasReturnableStocks) {
        setLoading(false);
        Swal.fire({
          icon: "info",
          title: "No Returnable Stocks",
          text: "All stocks from this purchase have already been returned. There are no stocks available for return.",
          confirmButtonColor: "#556ee6"
        });
        return;
      }

      // Set form data
      setForm({
        PurchaseOrder_Id: 0,
        PurchaseOrder_InvoiceNo: "",
        PurchaseOrder_RefNo: purchaseOrderData.purchaseOrder_RefNo || "",
        PurchaseOrder_Supplier_Id: purchaseOrderData.purchaseOrder_Supplier_Id,
        PurchaseOrder_InvoiceType_Id: purchaseOrderData.purchaseOrder_InvoiceType_Id,
        PurchaseOrder_Date: new Date(),
        PurchaseOrder_DueDate: purchaseOrderData.purchaseOrder_DueDate ? new Date(purchaseOrderData.purchaseOrder_DueDate) : new Date(),
        PurchaseOrder_Amount: 0,
        PurchaseOrder_DiscountPercent: 0,
        PurchaseOrder_DiscountAmount: 0,
        PurchaseOrder_OtherAmount: 0,
        PurchaseOrder_TaxAmount: 0,
        PurchaseOrder_OtherCharges: 0,
        PurchaseOrder_AdditionalDiscount: 0,
        PurchaseOrder_NetAmount: 0,
        PurchaseOrder_Notes: "",
        PurchaseOrder_TotalQty: 0,
        PurchaseOrder_Branch_Id: purchaseOrderData.purchaseOrder_Branch_Id,
        PurchaseOrder_TotalAmount: 0,
        PurchaseOrder_CreatedDate: new Date().toISOString(),
        PurchaseOrder_CreatedBy: getUserSession().userId,
        PurchaseOrder_Business_Id: purchaseOrderData.purchaseOrder_Business_Id,
        Supplier_NameEn: purchaseOrderData.supplier_NameEn,
        InvoiceType_Name: purchaseOrderData.invoiceType_Name
      });

      // Cleanup any existing locks before entering return mode
      if (temporaryFormUniqueId) {
        try {
          await deleteStockSelectionLock(temporaryFormUniqueId);
        } catch (error) {
          console.error('Error cleaning up locks before return mode:', error);
        }
      }
      
      // Set return mode
      setIsReturnMode(true);
      setReturnPurchaseOrderId(purchaseOrderData.purchaseOrder_Id);
      setOriginalInvoiceNo(purchaseOrderData.purchaseOrder_InvoiceNo);
      setIsEditing(false);
      setIsViewMode(false);
      // Reset form ID (will be generated when stock modal opens)
      setTemporaryFormUniqueId(null);

      // Load details
      if (purchaseOrderData.details && purchaseOrderData.details.length > 0) {
        const mappedDetails = purchaseOrderData.details.map((detail) => ({
          PurchaseOrderDet_Id: 0,
          PurchaseOrderDet_Product_Id: detail.purchaseOrderDet_Product_Id,
          PurchaseOrderDet_Unit_Id: detail.purchaseOrderDet_Unit_Id,
          PurchaseOrderDet_Quantity: 0,
          PurchaseOrderDet_BonusQuantity: 0,
          PurchaseOrderDet_UnitPrice: detail.purchaseOrderDet_UnitPrice,
          PurchaseOrderDet_DiscountPercent: detail.purchaseOrderDet_DiscountPercent || 0,
          PurchaseOrderDet_DiscountAmount: 0,
          PurchaseOrderDet_TotalAmount: 0,
          PurchaseOrderDet_NetAmount: 0,
          PurchaseOrderDet_TaxPercent: detail.purchaseOrderDet_TaxPercent || 0,
          PurchaseOrderDet_TaxAmount: 0,
          Product_NameEn: detail.product_NameEn,
          Unit_NameEn: detail.unit_NameEn,
          TaxDetails: detail.taxDetails || [],
          stockDetails: detail.stockDetails || [],
          originalQuantity: detail.purchaseOrderDet_Quantity,
          originalBonusQuantity: detail.purchaseOrderDet_BonusQuantity || 0
        }));
        // In return mode, hide products with no eligible stock
        const eligibleOnly = mappedDetails.filter(d => Array.isArray(d.stockDetails) && d.stockDetails.length > 0);
        setDetails(eligibleOnly.length > 0 ? eligibleOnly : [{ ...defaultDetail }]);
      } else {
        setDetails([{ ...defaultDetail }]);
      }

      // Generate return invoice number
      if (purchaseOrderData.purchaseOrder_Business_Id && purchaseOrderData.purchaseOrder_InvoiceNo) {
        try {
          const result = await getNextInvoiceNo(purchaseOrderData.purchaseOrder_Business_Id, true, purchaseOrderData.purchaseOrder_InvoiceNo);
          if (result.nextInvoiceNo) {
            setForm(prev => ({
              ...prev,
              PurchaseOrder_InvoiceNo: result.nextInvoiceNo
            }));
          }
        } catch (error) {
          console.error("Error getting return invoice number:", error);
        }
      }

      setActiveTab("1");
      setLoading(false);
    } catch (error) {
      console.error("Error loading purchase order for return:", error);
      Swal.fire("Error", "Failed to load purchase order for return", "error");
      setLoading(false);
    }
  };

  const handleStockSelectedForReturn = (stockData) => {
    const detailIndex = currentDetailIndex;
    const detail = details[detailIndex];
    
    if (!detail) return;

    // Calculate quantities from selected stock
    let regularQty = 0;
    let bonusQty = 0;
    
    stockData.forEach(group => {
      group.stocks.forEach(stock => {
        if (stock.purchaseStock_IsBonus || stock.purchaseStockIsBonus) {
          bonusQty += 1;
        } else {
          regularQty += 1;
        }
      });
    });

    // Update detail with new quantities
    const newDetails = [...details];
    newDetails[detailIndex] = {
      ...newDetails[detailIndex],
      PurchaseOrderDet_Quantity: regularQty,
      PurchaseOrderDet_BonusQuantity: bonusQty,
      selectedStockForReturn: stockData
    };

    // Recalculate amounts
    calculateDetailAmounts(newDetails, detailIndex);
    recalculateTotalsFromDetails(newDetails);
    setDetails(newDetails);

    // Store selected stock for this detail
    setSelectedStockForReturn(prev => ({
      ...prev,
      [detailIndex]: stockData
    }));

    setShowStockSelectionModal(false);
  };

  // Open stock details modal for view-only mode in returns
  const openStockDetailsModal = (index) => {
    const detail = details[index];
    const productName = getDetailField(detail, 'Product_NameEn');
    const stocks = detail?.stockDetails || [];
    console.log('[ReturnView] Opening StockDetailsModal', { index, productName, stockCount: stocks.length });
    setCurrentStockDetailsProduct(productName || 'Product');
    setCurrentStockDetails(stocks);
    setShowStockDetailsModal(true);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDetailChange = async (index, field, value) => {
    // Validate quantity limits
    if (field === 'PurchaseOrderDet_Quantity') {
      if (value > 5000) {
        Swal.fire({
          icon: 'warning',
          title: 'Quantity Limit Exceeded',
          text: 'Maximum quantity allowed is 5000. Please enter a value between 0 and 5000.',
          confirmButtonText: 'OK'
        });
        value = 5000; // Cap at 5000
      }
    } else if (field === 'PurchaseOrderDet_BonusQuantity') {
      if (value > 1000) {
        Swal.fire({
          icon: 'warning',
          title: 'Bonus Quantity Limit Exceeded',
          text: 'Maximum bonus quantity allowed is 1000. Please enter a value between 0 and 1000.',
          confirmButtonText: 'OK'
        });
        value = 1000; // Cap at 1000
      }
    }
    
    const newDetails = [...details];
    newDetails[index] = { ...newDetails[index], [field]: value };
    
    // Auto-fetch product details
    if (field === 'PurchaseOrderDet_Product_Id' && value) {
      const product = products.find(p => p.product_Id === value);
      if (product) {
        // Get unit information - extract only the name part
        const unit = units.find(u => u.value === product.unit_Id);
        const unitName = unit ? unit.label.split(' - ')[1] || unit.label : "Unit";
        
        // Calculate tax percentage from taxIds and populate TaxDetails
        let taxPercent = 0;
        let taxDetails = [];
        if (product.taxIds && product.taxIds.length > 0) {
          const productTaxes = taxes.filter(tax => product.taxIds.includes(tax.tax_Id));
          taxPercent = productTaxes.reduce((sum, tax) => sum + (tax.tax_Perc || 0), 0);
          taxDetails = productTaxes.map(tax => ({
            PurchaseOrderTaxDet_Tax_Id: tax.tax_Id,
            Tax_NameEn: tax.tax_NameEn,
            Tax_Perc: tax.tax_Perc
          }));
        }
        
        // Fetch previous unit price
        let previousUnitPrice = product.product_CostPrice || 0;
        try {
          const prices = await getProductPreviousPrices(value);
          console.log('=== UNIT PRICE DEBUG ===');
          console.log('Raw API response for unit price:', prices);
          console.log('PreviousUnitPrice from API:', prices?.previousUnitPrice);
          if (prices?.previousUnitPrice) {
            previousUnitPrice = prices.previousUnitPrice;
            console.log('Using previous unit price:', previousUnitPrice);
          } else {
            console.log('No previous unit price found, using product cost price:', previousUnitPrice);
          }
        } catch (error) {
          console.error('Error fetching previous unit price:', error);
        }
        
        newDetails[index] = {
          ...newDetails[index],
          PurchaseOrderDet_UnitPrice: previousUnitPrice,
          PurchaseOrderDet_TaxPercent: taxPercent,
          PurchaseOrderDet_Unit_Id: product.unit_Id || 1,
          Product_NameEn: product.product_NameEn,
          Unit_NameEn: unitName,
          expiryRequired: product.expiryRequired || false,
          TaxDetails: taxDetails
        };
      }
    }
    
    // Calculate amounts - trigger calculation for all relevant fields
    if (field === 'PurchaseOrderDet_Quantity' || field === 'PurchaseOrderDet_BonusQuantity' || 
        field === 'PurchaseOrderDet_UnitPrice' || field === 'PurchaseOrderDet_DiscountPercent' ||
        field === 'PurchaseOrderDet_TaxPercent') {
      calculateDetailAmounts(newDetails, index);
      
      // Recalculate totals when details change in edit mode
      if (isEditing) {
        recalculateTotalsFromDetails(newDetails);
      }
    }
    
    setDetails(newDetails);
  };

  const recalculateTotalsFromDetails = (detailsArray) => {
    // Calculate totals from details when user makes changes in edit mode
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    
    detailsArray.forEach((detail, index) => {
      if (detail && (detail.PurchaseOrderDet_Product_Id || detail.purchaseOrderDet_Product_Id)) {
        const totalAmountValue = parseFloat(detail.PurchaseOrderDet_TotalAmount || detail.purchaseOrderDet_TotalAmount) || 0;
        const discountAmount = parseFloat(detail.PurchaseOrderDet_DiscountAmount || detail.purchaseOrderDet_DiscountAmount) || 0;
        const taxAmount = parseFloat(detail.PurchaseOrderDet_TaxAmount || detail.purchaseOrderDet_TaxAmount) || 0;
        
        totalAmount += totalAmountValue;
        totalDiscount += discountAmount;
        totalTax += taxAmount;
      }
    });
    
    // Round footer components
    totalAmount = roundHalfUp(totalAmount, 2);
    totalDiscount = roundHalfUp(totalDiscount, 2);
    totalTax = roundHalfUp(totalTax, 2);

    const otherCharges = parseFloat(form.PurchaseOrder_OtherCharges) || 0;
    const additionalDiscount = parseFloat(form.PurchaseOrder_AdditionalDiscount) || 0;
    const netAmount = roundHalfUp(totalAmount + totalTax + otherCharges - additionalDiscount, 2);
    
    setForm(prev => ({
      ...prev,
      PurchaseOrder_TotalAmount: totalAmount,
      PurchaseOrder_DiscountAmount: totalDiscount,
      PurchaseOrder_TaxAmount: totalTax,
      PurchaseOrder_NetAmount: netAmount
    }));
  };

  const calculateDetailAmounts = (detailsArray, index) => {
    const detail = detailsArray[index];
    const quantity = parseFloat(detail.PurchaseOrderDet_Quantity) || 0;
    const bonusQuantity = parseFloat(detail.PurchaseOrderDet_BonusQuantity) || 0;
    const unitPrice = parseFloat(detail.PurchaseOrderDet_UnitPrice) || 0;
    const discountPercent = parseFloat(detail.PurchaseOrderDet_DiscountPercent) || 0;
    const taxPercent = parseFloat(detail.PurchaseOrderDet_TaxPercent) || 0;

    // If qty is zero, entire line should be zero regardless of bonus
    if (quantity <= 0) {
      detailsArray[index] = {
        ...detail,
        PurchaseOrderDet_DiscountAmount: 0,
        PurchaseOrderDet_TotalAmount: 0,
        PurchaseOrderDet_TaxAmount: 0,
        PurchaseOrderDet_NetAmount: 0
      };
      return;
    }

    // 1) Discounted unit price (rounded first)
    const discountedUnitPrice = roundHalfUp(unitPrice * (1 - (discountPercent / 100)), 2);

    // 2) Discount amount on purchased qty only
    const discountAmount = roundHalfUp(quantity * unitPrice * (discountPercent / 100), 2);

    // 3) Line total amount (qty × discounted unit)
    const lineTotalAmount = roundHalfUp(quantity * discountedUnitPrice, 2);

    // 4) Tax base on (qty + bonus) at discounted unit
    const totalQuantity = quantity + bonusQuantity;
    const taxBase = totalQuantity * discountedUnitPrice;

    // 5) Tax components (multiple taxes supported via TaxDetails); else fallback to taxPercent
    let taxComponentsSum = 0;
    if (Array.isArray(detail.TaxDetails) && detail.TaxDetails.length > 0) {
      taxComponentsSum = detail.TaxDetails.reduce((sum, t) => {
        const perc = parseFloat(t.Tax_Perc || t.tax_Perc || 0) || 0;
        const comp = roundHalfUp(taxBase * (perc / 100), 2);
        return sum + comp;
      }, 0);
    } else {
      taxComponentsSum = roundHalfUp(taxBase * (taxPercent / 100), 2);
    }
    const taxAmount = roundHalfUp(taxComponentsSum, 2);

    // 6) Net amount
    const netAmount = roundHalfUp(lineTotalAmount + taxAmount, 2);

    // Update detail with rounded values
    detailsArray[index] = {
      ...detail,
      PurchaseOrderDet_DiscountAmount: discountAmount,
      PurchaseOrderDet_TotalAmount: lineTotalAmount,
      PurchaseOrderDet_TaxAmount: taxAmount,
      PurchaseOrderDet_NetAmount: netAmount
    };
  };

  const addDetail = () => {
    // Validate that all products with selected items have stock details entered
    if (!isReturnMode) {
      for (let i = 0; i < details.length; i++) {
        const detail = details[i];
        // Only validate if product is selected
        if (detail.PurchaseOrderDet_Product_Id) {
          // Check if stock details exist and have sales price
          if (!detail.stockDetails || detail.stockDetails.length === 0) {
            Swal.fire({
              icon: 'error',
              title: 'Stock Details Required',
              text: `Please enter stock details (including sales price) for product "${detail.Product_NameEn || 'Unknown'}" before adding a new row.`,
              confirmButtonText: 'OK'
            });
            return;
          }
          
          // Check if all stock details have sales price entered
          const missingSalesPrice = detail.stockDetails.find((stock, index) => {
            const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
            return salesPrice <= 0;
          });
          
          if (missingSalesPrice) {
            const stockIndex = detail.stockDetails.findIndex((stock) => {
              const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
              return salesPrice <= 0;
            });
            Swal.fire({
              icon: 'error',
              title: 'Sales Price Required',
              text: `Please enter a sales price greater than 0 for stock item ${stockIndex + 1} of product "${detail.Product_NameEn || 'Unknown'}" before adding a new row.`,
              confirmButtonText: 'OK'
            });
            return;
          }
        }
      }
    }
    
    setDetails([...details, { ...defaultDetail }]);
  };

  const removeDetail = (index) => {
    if (details.length > 1) {
      const newDetails = details.filter((_, i) => i !== index);
      setDetails(newDetails);
    }
  };

  const openStockModal = async (index) => {
    console.log('=== OPEN STOCK MODAL DEBUG ===');
    console.log('Index:', index);
    console.log('Detail:', details[index]);
    
    const detail = details[index];
    const productId = getDetailField(detail, 'PurchaseOrderDet_Product_Id');
    
    console.log('Product ID found:', productId);
    console.log('Detail keys:', Object.keys(detail || {}));
    
    if (!detail || !productId) {
      console.log('No product ID found, showing error');
      Swal.fire("Error", "Please select a product first", "error");
      return;
    }

    const product = products.find(p => p.product_Id === productId);
    console.log('Product found:', product);
    setCurrentProduct(product);
    setCurrentDetailIndex(index);
    
    // If in return mode, use StockSelectionModal
    if (isReturnMode) {
      // Generate form ID when first product is opened in return mode
      if (!temporaryFormUniqueId) {
        const formId = generateTemporaryFormUniqueId();
        setTemporaryFormUniqueId(formId);
      }
      
      // Transform stockDetails from purchase order detail to format expected by StockSelectionModal
      const stockDetailsForModal = (detail.stockDetails || []).map(stock => ({
        purchaseStock_Id: stock.PurchaseStock_Id || stock.purchaseStock_Id,
        purchaseStock_Product_Id: stock.PurchaseStock_Product_Id || stock.purchaseStock_Product_Id,
        purchaseStock_Unit_Id: stock.PurchaseStock_Unit_Id || stock.purchaseStock_Unit_Id,
        purchaseStock_BatchNo: stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo,
        purchaseStock_ExpDate: stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate,
        purchaseStock_SalesPrice: stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice || 0,
        purchaseStock_CostPrice: stock.PurchaseStock_CostPrice || stock.purchaseStock_CostPrice || 0,
        purchaseStock_IsBonus: stock.PurchaseStock_IsBonus || stock.purchaseStock_IsBonus || false,
        purchaseStock_LastTransFlag: stock.PurchaseStock_LastTransFlag || stock.purchaseStock_LastTransFlag,
        discountPercent: 0 // Default discount for return
      }));
      
      setPreloadedStockItems(stockDetailsForModal);
      setShowStockSelectionModal(true);
      return;
    }
    
    // For regular purchase, use existing stock details modal
    // Fetch previous prices for the product
    try {
      const prices = await getProductPreviousPrices(productId);
      console.log('=== PREVIOUS PRICES DEBUG ===');
      console.log('Raw API response:', prices);
      console.log('HasPreviousPrices:', prices?.hasPreviousPrices);
      console.log('PreviousSalesPrice:', prices?.previousSalesPrice);
      console.log('PreviousUnitPrice:', prices?.previousUnitPrice);
      setPreviousPrices(prices);
    } catch (error) {
      console.error('Error fetching previous prices:', error);
      setPreviousPrices(null);
    }
    
    // Check if stock details already exist for this product
    if (detail.stockDetails && detail.stockDetails.length > 0) {
      console.log('Loading existing stock details:', detail.stockDetails);
      // Load existing stock details
      setStockDetails(detail.stockDetails);
    } else {
      console.log('Initializing new stock details');
      // Initialize new stock details
      const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
      const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
      
      console.log('Quantity:', quantity, 'Bonus Quantity:', bonusQuantity);
      
      const stockDetailsArray = [];
      for (let i = 0; i < quantity; i++) {
        stockDetailsArray.push({ ...defaultStock, PurchaseStock_IsBonus: false });
      }
      for (let i = 0; i < bonusQuantity; i++) {
        stockDetailsArray.push({ ...defaultStock, PurchaseStock_IsBonus: true });
      }
      
      console.log('Stock details array:', stockDetailsArray);
      setStockDetails(stockDetailsArray);
    }
    
    setShowStockModal(true);
  };

  const handleStockChange = (index, field, value) => {
    const newStockDetails = [...stockDetails];
    
    // Auto-format date for expiry date field
    if (field === 'PurchaseStock_ExpDate' && value) {
      let formattedValue = value.replace(/\D/g, ''); // Remove non-digits
      
      if (formattedValue.length >= 2) {
        formattedValue = formattedValue.substring(0, 2) + '/' + formattedValue.substring(2);
      }
      if (formattedValue.length >= 5) {
        formattedValue = formattedValue.substring(0, 5) + '/' + formattedValue.substring(5, 9);
      }
      
      newStockDetails[index] = { ...newStockDetails[index], [field]: formattedValue };
    } else {
      newStockDetails[index] = { ...newStockDetails[index], [field]: value };
    }
    
    // Auto-fill subsequent rows
    if (field === 'PurchaseStock_BatchNo' || field === 'PurchaseStock_ExpDate' || field === 'PurchaseStock_SalesPrice') {
      for (let i = index + 1; i < newStockDetails.length; i++) {
        newStockDetails[i] = { ...newStockDetails[i], [field]: newStockDetails[index][field] };
      }
    }
    
    setStockDetails(newStockDetails);
  };

  const saveStockDetails = () => {
    const detail = details[currentDetailIndex];
    if (!detail) {
      console.error("Detail not found at index:", currentDetailIndex);
      return;
    }
    
    // Check if product requires expiry date
    const productExpiryRequired = currentProduct?.expiryRequired || detail?.expiryRequired || false;
    
    // Validate expiry date if required
    if (productExpiryRequired) {
      // Check if expiry date is missing
      const missingExpiryDate = stockDetails.find((stock, index) => {
        const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
        return !expDate || expDate.trim() === '';
      });
      
      if (missingExpiryDate) {
        const stockIndex = stockDetails.findIndex((stock) => {
          const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
          return !expDate || expDate.trim() === '';
        });
        Swal.fire({
          icon: 'error',
          title: 'Expiry Date Required',
          text: `Please enter an expiry date for stock item ${stockIndex + 1}. This product requires an expiry date.`,
          confirmButtonText: 'OK'
        });
        return;
      }
      
      // Check if expiry date is valid
      const invalidExpiryDate = stockDetails.find((stock, index) => {
        const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
        if (!expDate || expDate.trim() === '') return false; // Already checked above
        return !isValidDate(expDate);
      });
      
      if (invalidExpiryDate) {
        const stockIndex = stockDetails.findIndex((stock) => {
          const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
          if (!expDate || expDate.trim() === '') return false;
          return !isValidDate(expDate);
        });
        Swal.fire({
          icon: 'error',
          title: 'Invalid Expiry Date',
          text: `Please enter a valid expiry date (DD/MM/YYYY format) for stock item ${stockIndex + 1}.`,
          confirmButtonText: 'OK'
        });
        return;
      }
    }
    
    // Validate that all sales prices are entered (greater than 0)
    const missingSalesPrice = stockDetails.find((stock, index) => {
      const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
      return salesPrice <= 0;
    });
    
    if (missingSalesPrice) {
      const stockIndex = stockDetails.findIndex((stock) => {
        const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
        return salesPrice <= 0;
      });
      Swal.fire({
        icon: 'error',
        title: 'Sales Price Required',
        text: `Please enter a sales price greater than 0 for stock item ${stockIndex + 1}.`,
        confirmButtonText: 'OK'
      });
      return;
    }
    
    const quantity = parseFloat(detail.PurchaseOrderDet_Quantity) || 0;
    const bonusQuantity = parseFloat(detail.PurchaseOrderDet_BonusQuantity) || 0;
    const totalQuantity = quantity + bonusQuantity;
    const totalAmount = parseFloat(detail.PurchaseOrderDet_Quantity * detail.PurchaseOrderDet_UnitPrice) || 0;
    
    console.log("=== SAVE STOCK DETAILS DEBUG ===");
    console.log("Current Detail Index:", currentDetailIndex);
    console.log("Product ID:", detail.PurchaseOrderDet_Product_Id);
    console.log("Quantity:", quantity, "Bonus Qty:", bonusQuantity, "Total Qty:", totalQuantity);
    console.log("Stock Details Array Length:", stockDetails.length);
    console.log("Stock Details Data:", stockDetails);
    
    // Calculate cost price: total amount / total quantity (qty + bonus qty)
    const costPrice = totalQuantity > 0 ? totalAmount / totalQuantity : 0;
    
    // Update stock details with cost price and required fields
    const updatedStockDetails = stockDetails.map((stock, index) => {
      const updatedStock = {
        ...stock,
        PurchaseStock_Product_Id: detail.PurchaseOrderDet_Product_Id,
        PurchaseStock_Unit_Id: detail.PurchaseOrderDet_Unit_Id,
        PurchaseStock_CostPrice: costPrice,
        PurchaseStock_IsBonus: stock.PurchaseStock_IsBonus || false
      };
      
      console.log(`Stock ${index + 1}:`, {
        BatchNo: updatedStock.PurchaseStock_BatchNo,
        ExpDate: updatedStock.PurchaseStock_ExpDate,
        SalesPrice: updatedStock.PurchaseStock_SalesPrice,
        CostPrice: updatedStock.PurchaseStock_CostPrice,
        IsBonus: updatedStock.PurchaseStock_IsBonus,
        ProductId: updatedStock.PurchaseStock_Product_Id,
        UnitId: updatedStock.PurchaseStock_Unit_Id
      });
      
      return updatedStock;
    });
    
    console.log("Updated Stock Details:", updatedStockDetails);
    
    // Store stock details in component state (NO API CALL)
    const newDetails = [...details];
    newDetails[currentDetailIndex] = {
      ...newDetails[currentDetailIndex],
      stockDetails: updatedStockDetails
    };
    
    // Add a new empty row automatically
    newDetails.push({
      ...defaultDetail,
      stockDetails: []
    });
    
    setDetails(newDetails);
    setShowStockModal(false);
  };

  const calculateTotals = () => {
    // In view mode, keep API values
    if (isViewMode) {
      return;
    }

    // Calculate totals from details for both new and edit entries
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    
    details.forEach((detail, index) => {
      if (detail && (detail.PurchaseOrderDet_Product_Id || detail.purchaseOrderDet_Product_Id)) {
        // Handle both field name variations
        const totalAmountValue = parseFloat(detail.PurchaseOrderDet_TotalAmount || detail.purchaseOrderDet_TotalAmount) || 0;
        const discountAmount = parseFloat(detail.PurchaseOrderDet_DiscountAmount || detail.purchaseOrderDet_DiscountAmount) || 0;
        const taxAmount = parseFloat(detail.PurchaseOrderDet_TaxAmount || detail.purchaseOrderDet_TaxAmount) || 0;
        
        totalAmount += totalAmountValue;
        totalDiscount += discountAmount;
        totalTax += taxAmount;
      }
    });
    
    // Round footer components
    totalAmount = roundHalfUp(totalAmount, 2);
    totalDiscount = roundHalfUp(totalDiscount, 2);
    totalTax = roundHalfUp(totalTax, 2);

    const otherCharges = parseFloat(form.PurchaseOrder_OtherCharges) || 0;
    const additionalDiscount = parseFloat(form.PurchaseOrder_AdditionalDiscount) || 0;
    const netAmount = roundHalfUp(totalAmount + totalTax + otherCharges - additionalDiscount, 2);
    
    setForm(prev => ({
      ...prev,
      PurchaseOrder_TotalAmount: totalAmount,
      PurchaseOrder_DiscountAmount: totalDiscount,
      PurchaseOrder_TaxAmount: totalTax,
      PurchaseOrder_NetAmount: netAmount
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [details, form.PurchaseOrder_OtherCharges, form.PurchaseOrder_AdditionalDiscount]);

  const handleSave = async () => {
    try {
      // Validation
      if (!form.PurchaseOrder_Business_Id) {
        Swal.fire("Error", "Please select a company", "error");
        return;
      }
      
      if (!form.PurchaseOrder_Supplier_Id) {
        Swal.fire("Error", "Please select a supplier", "error");
        return;
      }
      
      if (details.length === 0 || !details.some(detail => detail.PurchaseOrderDet_Product_Id)) {
        Swal.fire("Error", "Please add at least one product", "error");
        return;
      }

      // Remove empty rows (rows without product selected) before save
      const filteredDetails = details.filter(detail => detail.PurchaseOrderDet_Product_Id);
      if (filteredDetails.length === 0) {
        Swal.fire("Error", "Please add at least one product", "error");
        return;
      }
      
      // Validate stock details for all products (only for regular purchase mode)
      if (!isReturnMode) {
        for (let i = 0; i < filteredDetails.length; i++) {
          const detail = filteredDetails[i];
          
          // Check if stock details exist
          if (!detail.stockDetails || detail.stockDetails.length === 0) {
            Swal.fire({
              icon: 'error',
              title: 'Stock Details Required',
              text: `Please enter stock details (including sales price) for product "${detail.Product_NameEn || 'Unknown'}" before saving.`,
              confirmButtonText: 'OK'
            });
            return;
          }
          
          // Check if all stock details have sales price entered
          const missingSalesPrice = detail.stockDetails.find((stock, index) => {
            const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
            return salesPrice <= 0;
          });
          
          if (missingSalesPrice) {
            const stockIndex = detail.stockDetails.findIndex((stock) => {
              const salesPrice = parseFloat(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice) || 0;
              return salesPrice <= 0;
            });
            Swal.fire({
              icon: 'error',
              title: 'Sales Price Required',
              text: `Please enter a sales price greater than 0 for stock item ${stockIndex + 1} of product "${detail.Product_NameEn || 'Unknown'}" before saving.`,
              confirmButtonText: 'OK'
            });
            return;
          }
          
          const productExpiryRequired = detail?.expiryRequired || false;
          
          if (productExpiryRequired && detail.stockDetails && detail.stockDetails.length > 0) {
            // Check if expiry date is missing
            const missingExpiryDate = detail.stockDetails.find((stock, index) => {
              const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
              return !expDate || expDate.trim() === '';
            });
            
            if (missingExpiryDate) {
              const stockIndex = detail.stockDetails.findIndex((stock) => {
                const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
                return !expDate || expDate.trim() === '';
              });
              Swal.fire({
                icon: 'error',
                title: 'Expiry Date Required',
                text: `Please enter an expiry date for stock item ${stockIndex + 1} of product "${detail.Product_NameEn || 'Unknown'}". This product requires an expiry date.`,
                confirmButtonText: 'OK'
              });
              return;
            }
            
            // Check if expiry date is valid
            const invalidExpiryDate = detail.stockDetails.find((stock, index) => {
              const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
              if (!expDate || expDate.trim() === '') return false; // Already checked above
              return !isValidDate(expDate);
            });
            
            if (invalidExpiryDate) {
              const stockIndex = detail.stockDetails.findIndex((stock) => {
                const expDate = stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || '';
                if (!expDate || expDate.trim() === '') return false;
                return !isValidDate(expDate);
              });
              Swal.fire({
                icon: 'error',
                title: 'Invalid Expiry Date',
                text: `Please enter a valid expiry date (DD/MM/YYYY format) for stock item ${stockIndex + 1} of product "${detail.Product_NameEn || 'Unknown'}".`,
                confirmButtonText: 'OK'
              });
              return;
            }
          }
        }
      }
      
      // Create a mapping from original index to filtered index for selectedStockForReturn
      const originalToFilteredIndexMap = new Map();
      let filteredIndex = 0;
      details.forEach((detail, originalIndex) => {
        if (detail.PurchaseOrderDet_Product_Id) {
          originalToFilteredIndexMap.set(originalIndex, filteredIndex);
          filteredIndex++;
        }
      });
      
      // Create filtered selectedStockForReturn mapping
      const filteredSelectedStockForReturn = {};
      Object.keys(selectedStockForReturn || {}).forEach(originalIndexStr => {
        const originalIndex = parseInt(originalIndexStr);
        const newIndex = originalToFilteredIndexMap.get(originalIndex);
        if (newIndex !== undefined) {
          filteredSelectedStockForReturn[newIndex] = selectedStockForReturn[originalIndex];
        }
      });
      
      // Update state to remove empty rows from UI
      setDetails(filteredDetails);
      if (isReturnMode) {
        setSelectedStockForReturn(filteredSelectedStockForReturn);
      }

      // For return mode, show confirmation
      if (isReturnMode) {
        const result = await Swal.fire({
          title: 'Confirm Purchase Return',
          text: 'Are you sure you want to create this purchase return?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#556ee6',
          cancelButtonColor: '#74788d',
          confirmButtonText: 'Yes, Create Return',
          cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) {
          return;
        }
      }

      // Check stock locks before save (only for return mode)
      if (isReturnMode && temporaryFormUniqueId) {
        // Collect all selected stock IDs
        const allStockIds = [];
        filteredDetails.forEach((detail, index) => {
          if (detail.PurchaseOrderDet_Product_Id && filteredSelectedStockForReturn[index]) {
            filteredSelectedStockForReturn[index].forEach(group => {
              if (group.stocks && Array.isArray(group.stocks)) {
                group.stocks.forEach(stock => {
                  const stockId = stock.purchaseStock_Id || stock.PurchaseStock_Id;
                  if (stockId) {
                    allStockIds.push(stockId.toString());
                  }
                });
              }
            });
          }
        });
        
        if (allStockIds.length > 0) {
          try {
            const checkResult = await checkStockSelectionLocks(
              allStockIds.join(','),
              temporaryFormUniqueId
            );
            
            if (!checkResult.isSuccess || checkResult.hasLocks) {
              // Stocks are locked, prevent save and refresh stock modal
              Swal.fire({
                title: "Stock Locked",
                text: checkResult.message || "Some selected stocks are no longer available. Please refresh and select different stocks.",
                icon: "error",
                confirmButtonText: "OK"
              }).then(() => {
                // Refresh stock modal for the first detail with locked stock
                const firstDetailWithStock = filteredDetails.findIndex((detail, index) => 
                  detail.PurchaseOrderDet_Product_Id && filteredSelectedStockForReturn[index]
                );
                if (firstDetailWithStock >= 0) {
                  openStockModal(firstDetailWithStock);
                }
              });
              return;
            }
          } catch (error) {
            console.error('Error checking stock locks:', error);
            // Continue with save if check fails (don't block user)
          }
        }
      }

      setSaveLoading(true);
      const session = getUserSession();
      
      // Prepare stock details for return mode
      let stockDetailsForSave = [];
      if (isReturnMode) {
        // For return, use selected stock IDs from StockSelectionModal
        filteredDetails.forEach((detail, index) => {
          if (detail.PurchaseOrderDet_Product_Id && filteredSelectedStockForReturn[index]) {
            filteredSelectedStockForReturn[index].forEach(group => {
              group.stocks.forEach(stock => {
                stockDetailsForSave.push({
                  Product_Id: detail.PurchaseOrderDet_Product_Id,
                  Unit_Id: detail.PurchaseOrderDet_Unit_Id,
                  BatchNo: stock.purchaseStock_BatchNo || stock.PurchaseStock_BatchNo,
                  ExpDate: stock.purchaseStock_ExpDate || stock.PurchaseStock_ExpDate,
                  SalesPrice: stock.purchaseStock_SalesPrice || stock.PurchaseStock_SalesPrice || 0,
                  CostPrice: stock.purchaseStock_CostPrice || stock.PurchaseStock_CostPrice || detail.PurchaseOrderDet_UnitPrice,
                  IsBonus: stock.purchaseStock_IsBonus || stock.PurchaseStock_IsBonus || false,
                  PurchaseStock_Id: stock.purchaseStock_Id || stock.PurchaseStock_Id  // Include stock ID for return
                });
              });
            });
          }
        });
      } else {
        // For regular purchase, use existing logic
        stockDetailsForSave = filteredDetails
          .filter(detail => detail.PurchaseOrderDet_Product_Id && detail.stockDetails)
          .flatMap(detail => detail.stockDetails || []);
      }
      
      const saveData = {
        ...form,
        PurchaseOrder_InvoiceNo: form.PurchaseOrder_InvoiceNo,
        PurchaseOrder_CreatedBy: isEditing ? form.PurchaseOrder_CreatedBy : session.userId,
        IsReturn: isReturnMode ? true : false,
        Return_Purchase_Order_Id: isReturnMode ? returnPurchaseOrderId : null,
        Details: filteredDetails,
        StockDetails: stockDetailsForSave,
        TaxDetails: filteredDetails
          .filter(detail => detail.TaxDetails)
          .flatMap((detail, index) => {
            console.log(`=== TAX MAPPING PROCESS ===`);
            console.log(`Detail ${index + 1} (Product ${detail.PurchaseOrderDet_Product_Id}):`, detail.TaxDetails);
            const mappedTaxes = detail.TaxDetails.map(tax => ({
              ...tax,
              TempDetailId: index + 1 // Use index + 1 as TempDetailId
            }));
            console.log(`Mapped taxes for Detail ${index + 1}:`, mappedTaxes);
            return mappedTaxes;
          })
      };

      // Debug Tax Details Mapping
      console.log("=== TAX DETAILS MAPPING DEBUG ===");
      console.log("Total Details Count (before filter):", details.length);
      console.log("Filtered Details Count:", filteredDetails.length);
      
      // Debug each detail's tax mapping
      filteredDetails.forEach((detail, index) => {
        console.log(`Detail ${index + 1} (Product ${detail.PurchaseOrderDet_Product_Id}):`, {
          TaxDetails: detail.TaxDetails,
          TaxCount: detail.TaxDetails ? detail.TaxDetails.length : 0
        });
      });
      
      // Debug final TaxDetails array
      console.log("Final TaxDetails array being sent to API:", saveData.TaxDetails);
      saveData.TaxDetails.forEach((tax, index) => {
        console.log(`Tax ${index + 1}:`, {
          TempDetailId: tax.TempDetailId,
          Tax_Id: tax.PurchaseOrderTaxDet_Tax_Id,
          Tax_NameEn: tax.Tax_NameEn
        });
      });
      
      console.log("Full Save Data:", saveData);

      // Validate at least one selected stock in return mode
      if (isReturnMode && stockDetailsForSave.length === 0) {
        Swal.fire("Error", "Please select at least one stock item to return.", "error");
        setSaveLoading(false);
        return;
      }

      const result = await savePurchaseOrder(saveData);
      
      if (result && (result.id > 0 || result.PurchaseOrder_Id > 0)) {
        // Cleanup stock locks after successful save (only for return mode)
        if (isReturnMode && temporaryFormUniqueId) {
          try {
            await deleteStockSelectionLock(temporaryFormUniqueId);
          } catch (error) {
            console.error('Error cleaning up stock locks after save:', error);
          }
        }
        
        Swal.fire("Success", isReturnMode ? "Purchase return created successfully!" : "Purchase saved successfully!", "success");
        // Set loading to prevent rendering during reset
        setSaveLoading(true);
        // Clear the form and reset to initial state
        setForm({ 
          ...defaultForm,
          PurchaseOrder_Date: new Date(),
          PurchaseOrder_DueDate: new Date()
        });
        setDetails([{ ...defaultDetail }]);
        setIsEditing(false);
        // Reset form ID
        setTemporaryFormUniqueId(null);
        setIsReturnMode(false);
        setReturnPurchaseOrderId(null);
        setOriginalInvoiceNo(null);
        setSelectedStockForReturn({});
        setPreloadedStockItems(null);
        // Clear supplier dropdown state
        setSelectedSupplierOption(null);
        setSupplierSearchOptions([]);
        // Increment form key to force re-render of all Select components
        setFormKey(prev => prev + 1);
        // Add a delay to ensure state is reset before loading
        setTimeout(() => {
          loadPurchaseOrders();
          setSaveLoading(false);
        }, 200);
      } else {
        Swal.fire("Error", result.message || "Failed to save purchase", "error");
      }
    } catch (error) {
      console.error("Error saving purchase:", error);
      Swal.fire("Error", "Failed to save purchase", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (purchaseOrder) => {
    setForm({
      PurchaseOrder_Id: purchaseOrder.purchaseOrder_Id,
      PurchaseOrder_InvoiceNo: purchaseOrder.purchaseOrder_InvoiceNo,
      PurchaseOrder_RefNo: purchaseOrder.purchaseOrder_RefNo,
      PurchaseOrder_Supplier_Id: purchaseOrder.purchaseOrder_Supplier_Id,
      PurchaseOrder_InvoiceType_Id: purchaseOrder.purchaseOrder_InvoiceType_Id,
      PurchaseOrder_Date: purchaseOrder.purchaseOrder_Date ? new Date(purchaseOrder.purchaseOrder_Date) : new Date(),
      PurchaseOrder_DueDate: purchaseOrder.purchaseOrder_DueDate ? new Date(purchaseOrder.purchaseOrder_DueDate) : new Date(),
      PurchaseOrder_Amount: purchaseOrder.purchaseOrder_Amount,
      PurchaseOrder_DiscountPercent: purchaseOrder.purchaseOrder_DiscountPercent,
      PurchaseOrder_DiscountAmount: purchaseOrder.purchaseOrder_DiscountAmount,
      PurchaseOrder_OtherAmount: purchaseOrder.purchaseOrder_OtherAmount,
      PurchaseOrder_TaxAmount: purchaseOrder.purchaseOrder_TaxAmount,
      PurchaseOrder_OtherCharges: purchaseOrder.purchaseOrder_OtherCharges,
      PurchaseOrder_AdditionalDiscount: purchaseOrder.purchaseOrder_AdditionalDiscount,
      PurchaseOrder_NetAmount: purchaseOrder.purchaseOrder_NetAmount,
      PurchaseOrder_Notes: purchaseOrder.purchaseOrder_Notes,
      PurchaseOrder_TotalQty: purchaseOrder.purchaseOrder_TotalQty,
      PurchaseOrder_Branch_Id: purchaseOrder.purchaseOrder_Branch_Id,
      PurchaseOrder_TotalAmount: purchaseOrder.purchaseOrder_TotalAmount,
      PurchaseOrder_CreatedDate: purchaseOrder.purchaseOrder_CreatedDate,
      PurchaseOrder_CreatedBy: purchaseOrder.purchaseOrder_CreatedBy,
      PurchaseOrder_Business_Id: purchaseOrder.purchaseOrder_Business_Id,
      Supplier_NameEn: purchaseOrder.supplier_NameEn,
      InvoiceType_Name: purchaseOrder.invoiceType_Name
    });
    
    // Set selected supplier option for display
    if (purchaseOrder.purchaseOrder_Supplier_Id && purchaseOrder.supplier_NameEn) {
      // Look up supplier from loaded suppliers list to get the code
      const supplier = suppliers.find(s => s.supplier_Id === purchaseOrder.purchaseOrder_Supplier_Id);
      const supplierCode = supplier?.supplier_Code || purchaseOrder.supplier_Code || purchaseOrder.supplier_ContactNo || '';
      setSelectedSupplierOption({
        value: purchaseOrder.purchaseOrder_Supplier_Id,
        label: `${purchaseOrder.supplier_NameEn} — ${supplierCode}`
      });
    } else {
      setSelectedSupplierOption(null);
    }
    
    
    if (purchaseOrder.details && purchaseOrder.details.length > 0) {
      // Convert API field names to form field names and recalculate amounts using new rules
      const mappedDetails = purchaseOrder.details.map((detail) => ({
        PurchaseOrderDet_Id: detail.purchaseOrderDet_Id,
        PurchaseOrderDet_Product_Id: detail.purchaseOrderDet_Product_Id,
        PurchaseOrderDet_Unit_Id: detail.purchaseOrderDet_Unit_Id,
        PurchaseOrderDet_Quantity: detail.purchaseOrderDet_Quantity,
        PurchaseOrderDet_BonusQuantity: detail.purchaseOrderDet_BonusQuantity,
        PurchaseOrderDet_UnitPrice: detail.purchaseOrderDet_UnitPrice,
        PurchaseOrderDet_DiscountPercent: detail.purchaseOrderDet_DiscountPercent,
        PurchaseOrderDet_DiscountAmount: 0,
        PurchaseOrderDet_TotalAmount: 0,
        PurchaseOrderDet_NetAmount: 0,
        PurchaseOrderDet_TaxPercent: detail.purchaseOrderDet_TaxPercent,
        PurchaseOrderDet_TaxAmount: 0,
        Product_NameEn: detail.product_NameEn,
        Unit_NameEn: detail.unit_NameEn,
        TaxDetails: detail.taxDetails || [],
        stockDetails: detail.stockDetails || []
      }));

      // Recalculate for each detail with new logic
      const calcDetails = [...mappedDetails];
      for (let i = 0; i < calcDetails.length; i++) {
        calculateDetailAmounts(calcDetails, i);
      }
      setDetails(calcDetails);
      // Also refresh footer totals after mapping
      recalculateTotalsFromDetails(calcDetails);
    } else {
      setDetails([{ ...defaultDetail }]);
    }
    
    setIsEditing(true);
    setIsViewMode(false);
    setActiveTab("1");
  };

  const handleView = (purchaseOrder) => {
    console.log('=== HANDLEVIEW DEBUG ===');
    console.log('Raw purchaseOrder.details:', purchaseOrder.details);
    console.log('First detail raw data:', purchaseOrder.details?.[0]);
    console.log('First detail keys:', Object.keys(purchaseOrder.details?.[0] || {}));
    console.log('First detail purchaseOrderDet_Product_Id:', purchaseOrder.details?.[0]?.purchaseOrderDet_Product_Id);
    console.log('First detail purchaseOrderDet_Quantity:', purchaseOrder.details?.[0]?.purchaseOrderDet_Quantity);
    console.log('First detail product_NameEn:', purchaseOrder.details?.[0]?.product_NameEn);
    console.log('First detail unit_NameEn:', purchaseOrder.details?.[0]?.unit_NameEn);
    
    // Set selected supplier option for display
    if (purchaseOrder.purchaseOrder_Supplier_Id && purchaseOrder.supplier_NameEn) {
      // Look up supplier from loaded suppliers list to get the code
      const supplier = suppliers.find(s => s.supplier_Id === purchaseOrder.purchaseOrder_Supplier_Id);
      const supplierCode = supplier?.supplier_Code || purchaseOrder.supplier_Code || purchaseOrder.supplier_ContactNo || '';
      setSelectedSupplierOption({
        value: purchaseOrder.purchaseOrder_Supplier_Id,
        label: `${purchaseOrder.supplier_NameEn} — ${supplierCode}`
      });
    } else {
      setSelectedSupplierOption(null);
    }
    
    setForm({
      PurchaseOrder_Id: purchaseOrder.purchaseOrder_Id,
      PurchaseOrder_InvoiceNo: purchaseOrder.purchaseOrder_InvoiceNo,
      PurchaseOrder_RefNo: purchaseOrder.purchaseOrder_RefNo,
      PurchaseOrder_Supplier_Id: purchaseOrder.purchaseOrder_Supplier_Id,
      PurchaseOrder_InvoiceType_Id: purchaseOrder.purchaseOrder_InvoiceType_Id,
      PurchaseOrder_Date: purchaseOrder.purchaseOrder_Date ? new Date(purchaseOrder.purchaseOrder_Date) : new Date(),
      PurchaseOrder_DueDate: purchaseOrder.purchaseOrder_DueDate ? new Date(purchaseOrder.purchaseOrder_DueDate) : new Date(),
      PurchaseOrder_Amount: purchaseOrder.purchaseOrder_Amount,
      PurchaseOrder_DiscountPercent: purchaseOrder.purchaseOrder_DiscountPercent,
      PurchaseOrder_DiscountAmount: purchaseOrder.purchaseOrder_DiscountAmount,
      PurchaseOrder_OtherAmount: purchaseOrder.purchaseOrder_OtherAmount,
      PurchaseOrder_TaxAmount: purchaseOrder.purchaseOrder_TaxAmount,
      PurchaseOrder_OtherCharges: purchaseOrder.purchaseOrder_OtherCharges,
      PurchaseOrder_AdditionalDiscount: purchaseOrder.purchaseOrder_AdditionalDiscount,
      PurchaseOrder_NetAmount: purchaseOrder.purchaseOrder_NetAmount,
      PurchaseOrder_Notes: purchaseOrder.purchaseOrder_Notes,
      PurchaseOrder_TotalQty: purchaseOrder.purchaseOrder_TotalQty,
      PurchaseOrder_Branch_Id: purchaseOrder.purchaseOrder_Branch_Id,
      PurchaseOrder_TotalAmount: purchaseOrder.purchaseOrder_TotalAmount,
      PurchaseOrder_CreatedDate: purchaseOrder.purchaseOrder_CreatedDate,
      PurchaseOrder_CreatedBy: purchaseOrder.purchaseOrder_CreatedBy,
      PurchaseOrder_Business_Id: purchaseOrder.purchaseOrder_Business_Id,
      Supplier_NameEn: purchaseOrder.supplier_NameEn,
      InvoiceType_Name: purchaseOrder.invoiceType_Name
    });
    
    if (purchaseOrder.details && purchaseOrder.details.length > 0) {
      // Ensure TotalAmount is calculated for existing details in view mode
      const detailsWithCalculatedAmounts = purchaseOrder.details.map(detail => {
        if (!detail.PurchaseOrderDet_TotalAmount || detail.PurchaseOrderDet_TotalAmount === 0) {
          // Recalculate TotalAmount if missing
          const quantity = parseFloat(detail.PurchaseOrderDet_Quantity) || 0;
          const unitPrice = parseFloat(detail.PurchaseOrderDet_UnitPrice) || 0;
          const discountPercent = parseFloat(detail.PurchaseOrderDet_DiscountPercent) || 0;
          const baseAmount = quantity * unitPrice;
          const discountAmount = (baseAmount * discountPercent) / 100;
          const amountAfterDiscount = baseAmount - discountAmount;
          
          return {
            ...detail,
            PurchaseOrderDet_TotalAmount: amountAfterDiscount
          };
        }
        return detail;
      });
      setDetails(detailsWithCalculatedAmounts);
    } else {
      setDetails([{ ...defaultDetail }]);
    }
    
    setIsEditing(false);
    setIsViewMode(true);
    setActiveTab("1");
  };

  // View a purchase return (read-only) using new API
  const handleViewReturn = async (returnOrder) => {
    try {
      setLoading(true);
      const data = await getPurchaseReturnById(returnOrder.purchaseOrder_Id || returnOrder.PurchaseOrder_Id);
      if (!data || !data.header) {
        Swal.fire("Error", "Failed to load purchase return", "error");
        setLoading(false);
        return;
      }

      const header = data.header;
      const detailsResp = data.details || [];
      const returnedStock = data.returnedStock || [];

      setForm({
        PurchaseOrder_Id: header.purchaseOrder_Id,
        PurchaseOrder_InvoiceNo: header.purchaseOrder_InvoiceNo,
        PurchaseOrder_RefNo: header.purchaseOrder_RefNo,
        PurchaseOrder_Supplier_Id: header.purchaseOrder_Supplier_Id,
        PurchaseOrder_InvoiceType_Id: header.purchaseOrder_InvoiceType_Id,
        PurchaseOrder_Date: header.purchaseOrder_Date ? new Date(header.purchaseOrder_Date) : new Date(),
        PurchaseOrder_DueDate: header.purchaseOrder_DueDate ? new Date(header.purchaseOrder_DueDate) : new Date(),
        PurchaseOrder_Amount: header.purchaseOrder_Amount,
        PurchaseOrder_DiscountPercent: header.purchaseOrder_DiscountPercent,
        PurchaseOrder_DiscountAmount: header.purchaseOrder_DiscountAmount,
        PurchaseOrder_OtherAmount: header.purchaseOrder_OtherAmount,
        PurchaseOrder_TaxAmount: header.purchaseOrder_TaxAmount,
        PurchaseOrder_OtherCharges: header.purchaseOrder_OtherCharges,
        PurchaseOrder_AdditionalDiscount: header.purchaseOrder_AdditionalDiscount,
        PurchaseOrder_NetAmount: header.purchaseOrder_NetAmount,
        PurchaseOrder_Notes: header.purchaseOrder_Notes,
        PurchaseOrder_TotalQty: header.purchaseOrder_TotalQty,
        PurchaseOrder_Branch_Id: header.purchaseOrder_Branch_Id,
        PurchaseOrder_TotalAmount: header.purchaseOrder_TotalAmount,
        PurchaseOrder_CreatedDate: header.purchaseOrder_CreatedDate,
        PurchaseOrder_CreatedBy: header.purchaseOrder_CreatedBy,
        PurchaseOrder_Business_Id: header.purchaseOrder_Business_Id,
        Supplier_NameEn: header.supplier_NameEn,
        InvoiceType_Name: header.invoiceType_Name
      });

      // Map details and attach returned stock per detail
      const mapped = detailsResp.map(d => {
        // Prefer strict match by return detail id from StockTransaction
        let stocks = returnedStock.filter(s =>
          (s.Return_PurchaseOrderDet_Id || s.return_PurchaseOrderDet_Id) === d.purchaseOrderDet_Id
        );

        // Fallback: legacy payloads may only have original purchase detail id
        if (!stocks.length) {
          stocks = returnedStock.filter(s =>
            (s.PurchaseStock_PurchaseOrderDet_Id || s.purchaseStock_PurchaseOrderDet_Id) === d.purchaseOrderDet_Id
          );
        }

        // Final fallback: group by Product + Unit if no detail id linkage is present
        if (!stocks.length) {
          stocks = returnedStock.filter(s =>
            (s.PurchaseStock_Product_Id || s.purchaseStock_Product_Id) === d.purchaseOrderDet_Product_Id &&
            (s.PurchaseStock_Unit_Id || s.purchaseStock_Unit_Id) === d.purchaseOrderDet_Unit_Id
          );
        }

        // Minimal diagnostics for verification
        console.log('[ReturnView] Detail->Stock mapping', {
          detailId: d.purchaseOrderDet_Id,
          productId: d.purchaseOrderDet_Product_Id,
          unitId: d.purchaseOrderDet_Unit_Id,
          matchedStocks: stocks.length
        });

        const qty = stocks.filter(s => !(s.PurchaseStock_IsBonus || s.purchaseStock_IsBonus)).length;
        const bonusQty = stocks.filter(s => (s.PurchaseStock_IsBonus || s.purchaseStock_IsBonus)).length;

        return {
          PurchaseOrderDet_Id: d.purchaseOrderDet_Id,
          PurchaseOrderDet_Product_Id: d.purchaseOrderDet_Product_Id,
          PurchaseOrderDet_Unit_Id: d.purchaseOrderDet_Unit_Id,
          PurchaseOrderDet_Quantity: qty,
          PurchaseOrderDet_BonusQuantity: bonusQty,
          PurchaseOrderDet_UnitPrice: d.purchaseOrderDet_UnitPrice,
          PurchaseOrderDet_DiscountPercent: d.purchaseOrderDet_DiscountPercent || 0,
          PurchaseOrderDet_DiscountAmount: 0,
          PurchaseOrderDet_TotalAmount: 0,
          PurchaseOrderDet_NetAmount: 0,
          PurchaseOrderDet_TaxPercent: d.purchaseOrderDet_TaxPercent || 0,
          PurchaseOrderDet_TaxAmount: 0,
          Product_NameEn: d.product_NameEn,
          Unit_NameEn: d.unit_NameEn,
          TaxDetails: d.taxDetails || [],
          stockDetails: stocks
        };
      });

      // Calculate amounts and totals
      const calc = [...mapped];
      for (let i = 0; i < calc.length; i++) {
        calculateDetailAmounts(calc, i);
      }
      setDetails(calc);
      console.log('[ReturnView] Footer totals input snapshot', calc);
      recalculateTotalsFromDetails(calc);

      setIsReturnMode(true);
      setIsEditing(false);
      setIsViewMode(true);
      setActiveTab("1");
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load purchase return", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    // Cleanup stock locks before clear (only for return mode)
    if (isReturnMode && temporaryFormUniqueId) {
      deleteStockSelectionLock(temporaryFormUniqueId).catch(err => 
        console.error('Error cleaning up locks on clear:', err)
      );
    }
    
    setForm({ 
      ...defaultForm,
      PurchaseOrder_Date: new Date(),
      PurchaseOrder_DueDate: new Date()
    });
    setDetails([{ ...defaultDetail }]);
    setIsEditing(false);
    setIsViewMode(false);
    setSelectedSupplierOption(null);
    setSupplierSearchOptions([]);
    setFormKey(prev => prev + 1);
    setIsReturnMode(false);
    setReturnPurchaseOrderId(null);
    setOriginalInvoiceNo(null);
    setSelectedStockForReturn({});
    setPreloadedStockItems(null);
    setTemporaryFormUniqueId(null);
    setFormKey(prev => prev + 1);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(true);
  };

  // Options for dropdowns
  const businessOptions = useMemo(() => {
    return businesses.map(business => ({
      value: business.business_Id,
      label: business.business_NameEn
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    return branches
      .filter(branch => branch.branch_Buisness_Id === form.PurchaseOrder_Business_Id)
      .map(branch => ({
        value: branch.branch_Id,
        label: branch.branch_NameEn
      }));
  }, [branches, form.PurchaseOrder_Business_Id]);

  const supplierOptions = useMemo(() => {
    return suppliers.map(supplier => ({
      value: supplier.supplier_Id,
      label: supplier.supplier_NameEn
    }));
  }, [suppliers]);

  const productOptions = useMemo(() => {
    return products.map(product => ({
      value: product.product_Id,
      label: product.product_NameEn
    }));
  }, [products]);

  const unitOptions = useMemo(() => {
    return units.map(unit => ({
      value: unit.value,
      label: unit.label
    }));
  }, [units]);

  const invoiceTypeOptions = [
    { value: 1, label: "Cash" },
    { value: 2, label: "Credit" }
  ];

  // Derived lists for purchases vs returns (use API's isReturn flag strictly)
  const purchaseData = useMemo(() => (purchaseOrders || []).filter(po => po && po.isReturn !== true), [purchaseOrders]);

  const returnData = useMemo(() => (purchaseOrders || []).filter(po => po && po.isReturn === true), [purchaseOrders]);

  // Pagination
  const totalPages = Math.ceil((purchaseData.length || 0) / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = purchaseData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Diagnostics removed; API now supplies correct isReturn values

  if (!hasPermission) {
    return null;
  }

  const breadcrumbItem = activeTab === "1"
    ? (isReturnMode ? "Purchase Return" : "Add Purchase")
    : activeTab === "2"
      ? "Purchase List"
      : "Purchase Returns";

  return (
    <div className="page-content">
      <style>{`
        /* Hide spinner arrows for number inputs */
        input.no-spinner::-webkit-outer-spin-button,
        input.no-spinner::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input.no-spinner[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      <Container fluid>
        <Breadcrumb title="Purchase Management" breadcrumbItem={breadcrumbItem} />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>{isReturnMode ? "Purchase Return" : "Add Purchase"}</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Purchase List</span>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "3" ? "bold" : "normal",
                        color: activeTab === "3" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "3" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "3" ? "active" : ""}
                      onClick={() => setActiveTab("3")}
                    >
                      <i className="bx bx-reply"></i>
                      <span>Purchase Returns</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                        {/* Basic Information */}
                        <Row>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Company <span className="text-danger">*</span></Label>
                              <Select
                                key={`business-${formKey}`}
                                value={businessOptions.find(option => option.value === form.PurchaseOrder_Business_Id) || null}
                                onChange={(option) => handleBusinessChange(option?.value || null)}
                                options={businessOptions}
                                placeholder="Select Company"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={isViewMode || isReturnMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Branch</Label>
                              <Select
                                key={`branch-${formKey}`}
                                value={branchOptions.find(option => option.value === form.PurchaseOrder_Branch_Id) || null}
                                onChange={(option) => handleFormChange("PurchaseOrder_Branch_Id", option?.value || null)}
                                options={branchOptions}
                                placeholder="Select Branch"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={!form.PurchaseOrder_Business_Id || isViewMode || isReturnMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Invoice Number</Label>
                              <Input
                                type="text"
                                value={form.PurchaseOrder_InvoiceNo}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Date <span className="text-danger">*</span></Label>
                              <DatePicker
                                selected={form.PurchaseOrder_Date}
                                onChange={(date) => handleFormChange("PurchaseOrder_Date", date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                disabled={isViewMode || isReturnMode}
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Due Date</Label>
                              <DatePicker
                                selected={form.PurchaseOrder_DueDate}
                                onChange={(date) => handleFormChange("PurchaseOrder_DueDate", date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                disabled={isViewMode || isReturnMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Reference Number</Label>
                              <Input
                                type="text"
                                value={form.PurchaseOrder_RefNo}
                                onChange={(e) => handleFormChange("PurchaseOrder_RefNo", e.target.value)}
                                placeholder="Enter reference number"
                                disabled={isViewMode || isReturnMode}
                                readOnly={isReturnMode}
                                style={isReturnMode ? { backgroundColor: '#f8f9fa' } : {}}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Invoice Type <span className="text-danger">*</span></Label>
                              <Select
                                key={`invoiceType-${formKey}`}
                                value={invoiceTypeOptions.find(option => option.value === form.PurchaseOrder_InvoiceType_Id) || null}
                                onChange={(option) => handleFormChange("PurchaseOrder_InvoiceType_Id", option?.value || 1)}
                                options={invoiceTypeOptions}
                                styles={selectStyles}
                                isDisabled={isViewMode || isReturnMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Supplier <span className="text-danger">*</span></Label>
                              <Select
                                key={`supplier-${formKey}`}
                                value={selectedSupplierOption}
                                onChange={(option) => {
                                  setSelectedSupplierOption(option);
                                  handleFormChange("PurchaseOrder_Supplier_Id", option?.value || null);
                                }}
                                options={supplierSearchOptions}
                                placeholder="Search by name..."
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={isViewMode || isReturnMode}
                                isLoading={supplierSearchLoading}
                                noOptionsMessage={() => "Type at least 3 characters"}
                                loadingMessage={() => "Searching..."}
                                filterOption={null}
                                onInputChange={(inputValue) => {
                                  console.log('🔤 Input changed:', inputValue);
                                  
                                  // Clear previous timeout
                                  if (supplierSearchTimeout) {
                                    clearTimeout(supplierSearchTimeout);
                                  }
                                  
                                  // Trim the input value before searching
                                  const trimmedValue = inputValue ? inputValue.trim() : '';
                                  
                                  // Set new timeout
                                  const timeout = setTimeout(() => {
                                    if (trimmedValue.length >= 3) {
                                      searchSuppliersManually(trimmedValue);
                                    } else {
                                      setSupplierSearchOptions([]);
                                    }
                                  }, 300);
                                  setSupplierSearchTimeout(timeout);
                                  
                                  return inputValue;
                                }}
                              />
                            </div>
                          </Col>
                        </Row>

                        {/* Details Section */}
                        <Row>
                          <Col md={12}>
                            <Label className="fw-bold mb-3">Purchase Details</Label>
                            <div className="table-responsive">
                              <Table bordered style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Sr.No</th>
                                    <th width="20%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Product</th>
                                    <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit</th>
                                    <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Qty</th>
                                    <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Bonus Qty</th>
                                    <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit Price</th>
                                    <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Discount %</th>
                                    <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Tax %</th>
                                    <th width="12%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Total Amount</th>
                                    <th width="12%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Net Amount</th>
                                    <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {details
                                    .filter(detail => detail && getDetailField(detail, 'PurchaseOrderDet_Quantity') !== undefined)
                                    .filter(detail => !isReturnMode || (Array.isArray(detail.stockDetails) && detail.stockDetails.length > 0))
                                    .map((detail, index) => {
                                    return (
                                    <tr key={index}>
                                      <td style={{ padding: '0', textAlign: 'center', border: '1px solid #dee2e6' }}>{index + 1}</td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Select
                                          key={`product-${formKey}-${index}`}
                                          value={productOptions.find(option => option.value === getDetailField(detail, 'PurchaseOrderDet_Product_Id')) || null}
                                          onChange={(option) => handleDetailChange(index, 'PurchaseOrderDet_Product_Id', option?.value || null)}
                                          options={productOptions}
                                          placeholder="Select Product"
                                          styles={{
                                            ...selectStyles,
                                            control: (provided) => ({
                                              ...provided,
                                              border: 'none',
                                              borderRadius: '0',
                                              minHeight: '38px',
                                              boxShadow: 'none'
                                            })
                                          }}
                                          isClearable
                                          isSearchable
                                          maxMenuHeight={200}
                                          isDisabled={isViewMode || isReturnMode}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="text"
                                          value={getDetailField(detail, 'Unit_NameEn')}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                          placeholder="Select Product"
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          max="5000"
                                          step="1"
                                          value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_Quantity'))}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            // Only allow integers (whole numbers)
                                            if (value === '' || /^\d+$/.test(value)) {
                                              handleDetailChange(index, 'PurchaseOrderDet_Quantity', value === '' ? 0 : parseInt(value, 10) || 0);
                                            }
                                          }}
                                          onKeyDown={(e) => {
                                            // Prevent decimal point and other non-numeric characters except backspace, delete, arrow keys
                                            if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                              e.preventDefault();
                                            }
                                          }}
                                          disabled={isViewMode || isReturnMode}
                                          readOnly={isReturnMode}
                                          className="no-spinner"
                                          style={{ 
                                            border: 'none', 
                                            borderRadius: '0', 
                                            padding: '8px 12px',
                                            backgroundColor: isReturnMode ? '#f8f9fa' : 'white',
                                            appearance: 'none',
                                            MozAppearance: 'textfield'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          max="1000"
                                          step="1"
                                          value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_BonusQuantity'))}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            // Only allow integers (whole numbers)
                                            if (value === '' || /^\d+$/.test(value)) {
                                              handleDetailChange(index, 'PurchaseOrderDet_BonusQuantity', value === '' ? 0 : parseInt(value, 10) || 0);
                                            }
                                          }}
                                          onKeyDown={(e) => {
                                            // Prevent decimal point and other non-numeric characters except backspace, delete, arrow keys
                                            if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                                              e.preventDefault();
                                            }
                                          }}
                                          disabled={isViewMode || isReturnMode}
                                          readOnly={isReturnMode}
                                          className="no-spinner"
                                          style={{ 
                                            border: 'none', 
                                            borderRadius: '0', 
                                            padding: '8px 12px',
                                            backgroundColor: isReturnMode ? '#f8f9fa' : 'white',
                                            appearance: 'none',
                                            MozAppearance: 'textfield'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_UnitPrice'))}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            handleDetailChange(index, 'PurchaseOrderDet_UnitPrice', value === '' ? 0 : parseFloat(value) || 0);
                                          }}
                                          disabled={isViewMode || isReturnMode}
                                          readOnly={isReturnMode}
                                          className="no-spinner"
                                          style={{ 
                                            border: 'none', 
                                            borderRadius: '0', 
                                            padding: '8px 12px',
                                            backgroundColor: isReturnMode ? '#f8f9fa' : 'white',
                                            appearance: 'none',
                                            MozAppearance: 'textfield'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          max="100"
                                          step="0.01"
                                          value={formatNumericValue(getDetailField(detail, 'PurchaseOrderDet_DiscountPercent'))}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            handleDetailChange(index, 'PurchaseOrderDet_DiscountPercent', value === '' ? 0 : parseFloat(value) || 0);
                                          }}
                                          disabled={isViewMode || isReturnMode}
                                          readOnly={isReturnMode}
                                          className="no-spinner"
                                          style={{ 
                                            border: 'none', 
                                            borderRadius: '0', 
                                            padding: '8px 12px',
                                            backgroundColor: isReturnMode ? '#f8f9fa' : 'white',
                                            appearance: 'none',
                                            MozAppearance: 'textfield'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={getDetailField(detail, 'PurchaseOrderDet_TaxPercent')}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={getDetailField(detail, 'PurchaseOrderDet_TotalAmount') || getDetailField(detail, 'purchaseOrderDet_TotalAmount') || 0}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={getDetailField(detail, 'PurchaseOrderDet_NetAmount')}
                                          readOnly
                                          style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: 'none', 
                                            borderRadius: '0',
                                            padding: '8px 12px'
                                          }}
                                        />
                                      </td>
                                      <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', width: '5%' }}>
                                        <div className="d-flex justify-content-center align-items-center gap-1" style={{ minHeight: '38px' }}>
                                  {isReturnMode && !isViewMode && (
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => openStockModal(index)}
                                      disabled={!getDetailField(detail, 'PurchaseOrderDet_Product_Id')}
                                      style={{ 
                                        padding: '6px 10px',
                                        minWidth: '36px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '4px'
                                      }}
                                      title="Select Stock for Return"
                                    >
                                      <RotateCcw size={14} />
                                    </Button>
                                  )}
                                  {isReturnMode && isViewMode && (
                                    <Button
                                      type="button"
                                      color="info"
                                      size="sm"
                                      onClick={() => openStockDetailsModal(index)}
                                      disabled={!getDetailField(detail, 'PurchaseOrderDet_Product_Id')}
                                      style={{ 
                                        padding: '6px 10px',
                                        minWidth: '36px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '4px'
                                      }}
                                      title="View Stock Details"
                                    >
                                      <i className="bx bx-box" style={{ fontSize: '16px' }}></i>
                                    </Button>
                                  )}
                                  {!isReturnMode && (
                                    <Button
                                      type="button"
                                      color="info"
                                      size="sm"
                                      onClick={() => openStockModal(index)}
                                      disabled={!getDetailField(detail, 'PurchaseOrderDet_Product_Id')}
                                      style={{ 
                                        padding: '6px 10px',
                                        minWidth: '36px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '4px'
                                      }}
                                      title="Stock Details"
                                    >
                                      <i className="bx bx-box" style={{ fontSize: '16px' }}></i>
                                    </Button>
                                  )}
                                          {!isViewMode && details.length > 1 && (
                                            <Button
                                              type="button"
                                              color="danger"
                                              size="sm"
                                              onClick={() => removeDetail(index)}
                                              style={{ 
                                                padding: '6px 10px',
                                                minWidth: '36px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px'
                                              }}
                                              title="Remove Row"
                                            >
                                              <Minus size={14} />
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            </div>
                            {!isViewMode && !isReturnMode && (
                              <Button
                                type="button"
                                color="success"
                                size="sm"
                                onClick={addDetail}
                                className="mt-2"
                              >
                                <Plus size={16} /> Add Row
                              </Button>
                            )}
                          </Col>
                        </Row>

                        {/* Summary Section */}
                        <Row className="mt-4">
                          <Col md={6}>
                            <div className="mb-3">
                              <Label>Notes</Label>
                              <Input
                                type="textarea"
                                rows="3"
                                value={form.PurchaseOrder_Notes}
                                onChange={(e) => handleFormChange("PurchaseOrder_Notes", e.target.value)}
                                placeholder="Enter any notes"
                                disabled={isViewMode}
                              />
                            </div>
                          </Col>
                          <Col md={12}>
                            <Row>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Total Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseOrder_TotalAmount}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Total Discount Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseOrder_DiscountAmount}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Total Tax Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseOrder_TaxAmount}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Other Charges</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formatNumericValue(form.PurchaseOrder_OtherCharges)}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      handleFormChange("PurchaseOrder_OtherCharges", value === '' ? 0 : parseFloat(value) || 0);
                                    }}
                                    disabled={isViewMode}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Additional Discount</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formatNumericValue(form.PurchaseOrder_AdditionalDiscount)}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      handleFormChange("PurchaseOrder_AdditionalDiscount", value === '' ? 0 : parseFloat(value) || 0);
                                    }}
                                    disabled={isViewMode}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label className="fw-bold">Net Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseOrder_NetAmount}
                                    readOnly
                                    style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        </Row>

                        {!isViewMode && (
                          <Row className="mt-4">
                            <Col>
                              <div className="d-flex gap-2">
                                <Button
                                  type="submit"
                                  color="primary"
                                  disabled={saveLoading}
                                  className="waves-effect waves-light"
                                >
                                  {saveLoading && (
                                    <ClipLoader size={16} color="#ffffff" className="me-2" />
                                  )}
                                  {isReturnMode ? "Create Purchase Return" : (isEditing ? "Update Purchase" : "Save Purchase")}
                                </Button>
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={handleClear}
                                  className="waves-effect waves-light"
                                >
                                  Clear
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        )}
                      </Form>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Purchase</h5>
                            <Button
                              color="primary"
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={3}>
                                <Label>Company</Label>
                                <Select
                                  value={businessOptions.find(option => option.value === filter.PurchaseOrder_Business_Id)}
                                  onChange={(option) => setFilter({ ...filter, PurchaseOrder_Business_Id: option?.value || null })}
                                  options={businessOptions}
                                  placeholder="Select Company"
                                  styles={selectStyles}
                                  isClearable
                                />
                              </Col>
                              <Col md={3}>
                                <Label>Supplier</Label>
                                <Select
                                  value={selectedSupplierFilterOption}
                                  onChange={(option) => {
                                    setSelectedSupplierFilterOption(option);
                                    setFilter({ ...filter, PurchaseOrder_Supplier_Id: option?.value || null });
                                  }}
                                  options={supplierSearchOptions}
                                  placeholder="Search by name..."
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                  isLoading={supplierSearchLoading}
                                  noOptionsMessage={() => "Type at least 3 characters"}
                                  loadingMessage={() => "Searching..."}
                                  filterOption={null}
                                  onInputChange={(inputValue) => {
                                    console.log('🔤 Filter Input changed:', inputValue);
                                    
                                    // Clear previous timeout
                                    if (supplierSearchTimeout) {
                                      clearTimeout(supplierSearchTimeout);
                                    }
                                    
                                    // Trim the input value before searching
                                    const trimmedValue = inputValue ? inputValue.trim() : '';
                                    
                                    // Set new timeout
                                    const timeout = setTimeout(() => {
                                      if (trimmedValue.length >= 3) {
                                        searchSuppliersFilterManually(trimmedValue);
                                      } else {
                                        setSupplierSearchOptions([]);
                                      }
                                    }, 300);
                                    setSupplierSearchTimeout(timeout);
                                    
                                    return inputValue;
                                  }}
                                />
                              </Col>
                              <Col md={3}>
                                <Label>From Date</Label>
                                <DatePicker
                                  selected={filter.From_Date}
                                  onChange={(date) => setFilter({ ...filter, From_Date: date })}
                                  dateFormat="dd/MM/yyyy"
                                  className="form-control"
                                  isClearable
                                />
                              </Col>
                              <Col md={3}>
                                <Label>To Date</Label>
                                <DatePicker
                                  selected={filter.To_Date}
                                  onChange={(date) => setFilter({ ...filter, To_Date: date })}
                                  dateFormat="dd/MM/yyyy"
                                  className="form-control"
                                  isClearable
                                />
                              </Col>
                              <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Purchase List
                            </h5>
                            {hasEditOrHigher && (
                              <Button
                                color="primary"
                                onClick={() => setActiveTab("1")}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Purchase
                              </Button>
                            )}
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading purchase...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="4%">#</th>
                                      <th width="12%">Date</th>
                                      <th width="15%">Invoice Number</th>
                                      <th width="15%">Supplier</th>
                                      <th width="10%">Net Amount</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedData.length > 0 ? (
                                      paginatedData.map((item, index) => (
                                        <tr key={`purchase-order-${item.purchaseOrder_Id || index}`}>
                                          <td>{startIndex + index + 1}</td>
                                          <td>{item.purchaseOrder_Date ? formatDateDDMMYYYY(item.purchaseOrder_Date) : 'N/A'}</td>
                                          <td>{item.purchaseOrder_InvoiceNo || 'N/A'}</td>
                                          <td>{item.supplier_NameEn || 'N/A'}</td>
                                          <td>{Number(item.purchaseOrder_NetAmount ?? 0).toFixed(2)}</td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(item)}
                                                className="waves-effect waves-light"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              {/* {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === item.PurchaseOrder_Id}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === item.PurchaseOrder_Id ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )} */}
                                              {hasDeletePermission && (
                                                <Button
                                                  color="warning"
                                                  size="sm"
                                                  onClick={() => handlePurchaseReturn(item)}
                                                  className="waves-effect waves-light"
                                                  title="Purchase Return"
                                                >
                                                  <RotateCcw size={12} />
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="6" className="text-center">
                                          {hasAppliedFilter ? "No purchase found matching the filter criteria." : "No purchase available. Please add some purchase first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {startIndex + 1} to {Math.min(endIndex, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {totalPages}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === totalPages}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>

                  {/* Purchase Returns Tab */}
                  <TabPane tabId="3">
                    <div>
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">Purchase Returns</h5>
                          </div>
                          <div className="table-responsive">
                            <Table className="table table-bordered table-striped table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th width="4%">#</th>
                                  <th width="12%">Date</th>
                                  <th width="20%">Return Invoice</th>
                                  <th width="15%">Supplier</th>
                                  <th width="10%">Net Amount</th>
                                  <th width="10%">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {returnData.length > 0 ? (
                                  returnData.map((item, idx) => (
                                    <tr key={`purchase-return-${item.purchaseOrder_Id || idx}`}>
                                      <td>{idx + 1}</td>
                                      <td>{item.purchaseOrder_Date ? formatDateDDMMYYYY(item.purchaseOrder_Date) : 'N/A'}</td>
                                      <td>{item.purchaseOrder_InvoiceNo || 'N/A'}</td>
                                      <td>{item.supplier_NameEn || 'N/A'}</td>
                                      <td>{Number(item.purchaseOrder_NetAmount ?? 0).toFixed(2)}</td>
                                      <td>
                                        <div className="d-flex gap-2">
                                          <Button
                                            color="info"
                                            size="sm"
                                            onClick={() => handleViewReturn(item)}
                                            className="waves-effect waves-light"
                                          >
                                            <Eye size={12} />
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="6" className="text-center">No purchase returns available.</td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Stock Selection Modal for Purchase Return */}
      {isReturnMode && (
        <StockSelectionModal
          isOpen={showStockSelectionModal}
          toggle={() => setShowStockSelectionModal(false)}
          productId={currentProduct?.product_Id}
          productName={currentProduct?.product_NameEn}
          requestedQuantity={details[currentDetailIndex]?.originalQuantity || 0}
          onStockSelected={handleStockSelectedForReturn}
          selectedStockItems={selectedStockForReturn[currentDetailIndex] || []}
          branchId={form.PurchaseOrder_Branch_Id}
          businessId={form.PurchaseOrder_Business_Id}
          preloadedStockItems={preloadedStockItems}
          temporaryFormUniqueId={temporaryFormUniqueId}
          flag={3}
          createdBy={getUserSession().userId}
        />
      )}

      {/* View-only Stock Details Modal for Purchase Return */}
      {isReturnMode && isViewMode && (
        <StockDetailsModal
          isOpen={showStockDetailsModal}
          toggle={() => setShowStockDetailsModal(false)}
          productName={currentStockDetailsProduct}
          stockDetails={currentStockDetails}
        />
      )}

      {/* Stock Details Modal */}
      <Modal isOpen={showStockModal} toggle={() => setShowStockModal(false)} size="xl">
        <style>{`
          .stock-details-modal-content {
            font-size: 0.8125rem;
          }
          .stock-details-modal-content .table {
            font-size: 0.8125rem;
          }
          .stock-details-modal-content .table th,
          .stock-details-modal-content .table td {
            font-size: 0.8125rem;
            padding: 0.4rem 0.5rem;
          }
          .stock-details-modal-content .form-control {
            font-size: 0.8125rem;
            padding: 0.375rem 0.5rem;
          }
          .stock-details-modal-content .badge {
            font-size: 0.6875rem;
            padding: 0.25rem 0.5rem;
          }
          .stock-details-modal-header {
            font-size: 0.9375rem;
          }
          .stock-details-modal-header .modal-title {
            font-size: 0.9375rem;
          }
        `}</style>
        <ModalHeader toggle={() => setShowStockModal(false)} className="stock-details-modal-header">
          Stock Details - {currentProduct?.product_NameEn}
          {previousPrices?.hasPreviousPrices && previousPrices?.previousSalesPrice && (
            <span className="ms-3 text-muted">
              (Last Sales Price: {parseFloat(previousPrices.previousSalesPrice).toFixed(2)})
            </span>
          )}
        </ModalHeader>
        <ModalBody className="stock-details-modal-content">
          <div className="table-responsive">
            <Table bordered>
              <thead className="table-light">
                <tr>
                  <th width="5%">Sr.No</th>
                  <th width="15%">Batch Number</th>
                  <th width="15%">Expiry Date</th>
                  <th width="15%">Sales Price</th>
                  <th width="15%">Cost Price</th>
                  <th width="10%">Type</th>
                </tr>
              </thead>
              <tbody>
                {stockDetails.map((stock, index) => {
                  return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <Input
                        type="text"
                        value={stock.PurchaseStock_BatchNo || stock.purchaseStock_BatchNo || ''}
                        onChange={(e) => handleStockChange(index, 'PurchaseStock_BatchNo', e.target.value)}
                        placeholder="Enter batch number"
                        disabled={isViewMode}
                      />
                    </td>
                    <td>
                      {currentProduct?.expiryRequired ? (
                        <Input
                          type="text"
                          value={stock.PurchaseStock_ExpDate || stock.purchaseStock_ExpDate || ''}
                          onChange={(e) => {
                            const dateStr = e.target.value;
                            // Allow typing and store as string
                            handleStockChange(index, 'PurchaseStock_ExpDate', dateStr);
                          }}
                          placeholder="DD/MM/YYYY"
                          maxLength="10"
                          disabled={isViewMode}
                        />
                      ) : (
                        <Input
                          type="text"
                          value="N/A"
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      )}
                    </td>
                    <td>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formatNumericValue(stock.PurchaseStock_SalesPrice || stock.purchaseStock_SalesPrice || 0)}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleStockChange(index, 'PurchaseStock_SalesPrice', value === '' ? 0 : parseFloat(value) || 0);
                        }}
                        placeholder="Sales price"
                        disabled={isViewMode}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={stock.purchaseStock_CostPrice || stock.PurchaseStock_CostPrice || (() => {
                          const detail = details[currentDetailIndex];
                          if (!detail) return 0;
                          const quantity = parseFloat(detail.purchaseOrderDet_Quantity || detail.PurchaseOrderDet_Quantity) || 0;
                          const bonusQuantity = parseFloat(detail.purchaseOrderDet_BonusQuantity || detail.PurchaseOrderDet_BonusQuantity) || 0;
                          const totalQuantity = quantity + bonusQuantity;
                          const unitPrice = parseFloat(detail.purchaseOrderDet_UnitPrice || detail.PurchaseOrderDet_UnitPrice) || 0;
                          const totalAmount = parseFloat(quantity * unitPrice) || 0;
                          return totalQuantity > 0 ? (totalAmount / totalQuantity).toFixed(2) : 0;
                        })()}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </td>
                    <td>
                      <span className={`badge ${(stock.purchaseStock_IsBonus || stock.PurchaseStock_IsBonus) ? 'bg-warning' : 'bg-primary'}`}>
                        {(stock.purchaseStock_IsBonus || stock.PurchaseStock_IsBonus) ? 'Bonus' : 'Regular'}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          {!isViewMode && (
            <Button color="primary" onClick={saveStockDetails}>
              Save Stock Details
            </Button>
          )}
          <Button color="secondary" onClick={() => setShowStockModal(false)}>
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
